import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, BarChart3, CheckCheck, CheckCircle2, Circle, FileText, Hourglass, Network, Package, Plus, Save, Trash2, Undo2, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge, Btn, Card, Chip, Confirm, Empty, Field, FilePickButton, IconBtn, InfoRow, Loading, MediaViewer, Page, Section, Select, StatusPill, cx } from "../components/wf";
import { api, fileUrl, money, num, openLink, todayISO, uploadFile } from "../lib/api";
import { usePerms } from "../lib/auth";
import { useAuth } from "../lib/auth";
import { useEmployees, useKastle, useStatuses } from "../lib/queries";
import { RejectTaskDialog, TaskPartsDialog } from "../components/ProjectTaskDialogs";
import { GanttChart } from "../components/Gantt";
import { emailResultMessage } from "../lib/api";

const PRIORITY_TONE = { high: "over", medium: "warn", low: "ok" };

export function Projects() {
  const q = useQuery({ queryKey: ["projects"], queryFn: () => api("/projects") });
  return (
    <Page title="Projects" subtitle={`${q.data?.length ?? 0} projects`} wide testid="projects-page">
      {q.isLoading ? <Loading /> : !q.data?.length ? <Empty icon={Network} title="No projects yet" subtitle="Open a job and click Add Project to turn it into a project." /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-list">
          {q.data.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`} className="rounded-2xl border border-wf-line p-5 transition-colors hover:border-wf-brand2 hover:bg-wf-s2">
              <div className="flex items-start justify-between gap-2"><p className="truncate text-[17px] font-bold">{p.customer_name}</p><StatusPill label={p.status} /></div>
              <p className="mt-1 line-clamp-2 text-sm text-wf-ink2">{p.job_description}</p>
              <div className="mt-3 h-2 overflow-hidden rounded bg-wf-s3"><div className="h-2 rounded bg-wf-ok" style={{ width: `${p.tasks_total ? (p.tasks_done / p.tasks_total) * 100 : 0}%` }} /></div>
              <p className="mt-2 text-[13px] font-semibold text-wf-mute">
                {p.tasks_done} of {p.tasks_total} items done{p.tasks_overdue ? <span className="text-wf-err"> · {p.tasks_overdue} overdue</span> : null}{p.next_due ? ` · next due ${p.next_due}` : ""}
              </p>
              {!!p.awaiting_approval && (
                <p data-testid={`project-awaiting-${p.id}`} className="mt-1 flex items-center gap-1 text-[13px] font-semibold text-wf-warn">
                  <CheckCheck size={14} /> {p.awaiting_approval} item{p.awaiting_approval === 1 ? "" : "s"} waiting for your approval
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}

function TaskPhotos({ t, kind, onAdd, onRemove, busy, open }) {
  const list = t[`${kind}_photos`] ?? [];
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-wf-mute">{kind} ({list.length})</span>
        <FilePickButton testid={`task-${t.id}-${kind}-add`} variant="ghost" accept="image/*" multiple loading={busy} onFiles={(f) => onAdd(kind, f)} className="!h-7 !px-2 text-xs">+ Add</FilePickButton>
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {list.map((p) => (
          <div key={p} className="group relative">
            <button onClick={() => open(p)}><img src={fileUrl(p)} alt="" className="h-16 w-16 rounded-lg bg-wf-s3 object-cover" /></button>
            <button onClick={() => onRemove(kind, p)} data-testid="task-photo-remove" className="absolute -right-1.5 -top-1.5 hidden rounded-full bg-wf-err p-0.5 text-white group-hover:block"><X size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isManager, seeMoney } = usePerms();
  const q = useQuery({ queryKey: ["project", id], queryFn: () => api(`/projects/${id}`) });
  const emps = useEmployees();
  const kastle = useKastle();
  const statuses = useStatuses();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [rejectTask, setRejectTask] = useState(null);
  const [partsTask, setPartsTask] = useState(null);
  const [chart, setChart] = useState(false);
  const { user } = useAuth();
  const p = q.data;
  const run = async (fn, msg) => {
    try {
      const res = await fn();
      qc.setQueryData(["project", id], res);
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["work-orders"] });
      qc.invalidateQueries({ queryKey: ["work-order", res.work_order_id] });
      if (res.email_result) (res.email_result.ok ? toast.success : toast.error)(emailResultMessage(res.email_result));
      else if (msg) toast.success(msg);
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    }
  };
  if (q.isLoading) return <Loading />;
  if (!p) return <Page title="Project" back><Empty title="Could not load this project" subtitle={q.error?.message} /></Page>;
  const name = (eid) => emps.data?.find((e) => e.id === eid)?.name ?? "";
  const t = p.job.totals ?? {};
  const addPhotos = async (tid, kind, files) => {
    setBusy(tid + kind);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadFile(f));
      await run(() => api(`/projects/${id}/tasks/${tid}/photos`, { method: "POST", body: { kind, paths } }), "Photos added");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };
  const today = todayISO();
  const myEid = user?.employee_id;
  const canComplete = (x) => isManager || (!!myEid && (x.lead_tech_id === myEid || x.helper_ids?.includes(myEid)));
  const onJob = p.on_job !== false;
  const tasks = [...p.tasks].sort((a, b) => Number(!!a.completed_date) - Number(!!b.completed_date) || (a.due_date || "9").localeCompare(b.due_date || "9"));

  return (
    <Page title={p.job.customer?.name ?? "Project"} subtitle={`Project · ${p.job.job_description}`} back wide testid="project-detail"
      right={<>
        <Btn variant="secondary" icon={BarChart3} data-testid="project-gantt-button" onClick={() => openLink(`/projects/${id}/gantt-link`).catch((e) => toast.error(e.message || "Could not create the PDF"))}>Gantt PDF</Btn>
        <Btn variant="secondary" icon={FileText} data-testid="project-report-button" title="Every item with crew, dates, approvals, hours and photos, no prices" onClick={() => openLink(`/projects/${id}/report-link`).catch((e) => toast.error(e.message))}>Report PDF</Btn>
        {isManager && <IconBtn icon={Trash2} tone="danger" onClick={() => setConfirm(true)} data-testid="project-delete-button" />}
      </>}>
      <div className="grid gap-8 lg:grid-cols-[340px_1fr]">
        <div>
          <Card className="py-2">
            <InfoRow label="Customer" value={p.job.customer?.name} />
            <InfoRow label="Job" value={p.job.job_description} />
          </Card>
          {onJob && <Link to={`/work-orders/${p.work_order_id}`} data-testid="project-open-job" className="mt-2 inline-block text-sm font-semibold text-wf-brand hover:underline">Open the job →</Link>}
          <Section title="Status" />
          <Select testid="project-status-dropdown" value={p.job.status} options={(statuses.data ?? []).map((s) => s.name)}
            onChange={(v) => run(() => api(`/projects/${id}/status`, { method: "PUT", body: { status: v } }), `Status: ${v}`)} />
          <Section title="Project Managers" />
          {isManager ? (
            <>
              <Select label="Kastle Project Manager" testid="project-kastle-pm" value={p.kastle_pm_id} placeholder="None" options={(kastle.data ?? []).map((k) => ({ value: k.id, label: k.name }))}
                onChange={(v) => run(() => api(`/projects/${id}`, { method: "PUT", body: { kastle_pm_id: v, company_pm_id: p.company_pm_id } }))} />
              <Select label="Company Project Manager" testid="project-company-pm" value={p.company_pm_id} placeholder="None"
                options={(emps.data ?? []).filter((e) => (e.status ?? "active") === "active" || e.id === p.company_pm_id).map((e) => ({ value: e.id, label: e.name }))}
                onChange={(v) => run(() => api(`/projects/${id}`, { method: "PUT", body: { kastle_pm_id: p.kastle_pm_id, company_pm_id: v } }))} />
            </>
          ) : (
            <Card className="py-2"><InfoRow label="Kastle PM" value={p.job.kastle_contact?.name} /><InfoRow label="Company PM" value={name(p.company_pm_id)} /></Card>
          )}
          {seeMoney && (
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[["Working Budget", t.working_budget], ["Project Total", t.total_cost], ["Remaining", t.budget_remaining]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-wf-line bg-wf-s2 p-3"><p className="text-[11px] font-semibold text-wf-mute">{l}</p><p className={cx("truncate font-extrabold", l === "Remaining" && v < 0 && "text-wf-err")}>{money(v ?? 0)}</p></div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Section title={`Items To Complete · ${p.tasks_done}/${p.tasks_total}`} actionLabel={isManager ? "Add Item" : undefined} testid="project-add-task" onAction={() => nav(`/projects/${id}/tasks/new`)} />
          {p.can_approve && !!p.awaiting_approval && (
            <p data-testid="project-approval-banner" className="mb-3 flex items-center gap-2 rounded-xl bg-orange-50 p-3 text-sm font-bold text-orange-800">
              <CheckCheck size={17} /> {p.awaiting_approval} completed item{p.awaiting_approval === 1 ? " needs" : "s need"} your approval
            </p>
          )}
          {p.tasks_overdue > 0 && (
            <p data-testid="project-overdue-banner" className="mb-3 flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">
              <AlertCircle size={17} /> {p.tasks_overdue} item{p.tasks_overdue === 1 ? " is" : "s are"} past the due date
            </p>
          )}
          <div className="mb-3 inline-flex rounded-xl bg-wf-s3 p-1">
            {[[false, "List"], [true, "Gantt Chart"]].map(([v, l]) => (
              <button key={l} data-testid={`project-view-${v ? "gantt" : "list"}`} onClick={() => setChart(v)} className={cx("h-8 rounded-lg px-4 text-sm font-semibold", chart === v ? "bg-wf-surface shadow-sm" : "text-wf-mute")}>{l}</button>
            ))}
          </div>
          {chart && <GanttChart tasks={p.tasks} onPick={(t) => isManager && nav(`/projects/${id}/tasks/${t.id}`)} />}
          {!chart && !p.tasks.length && <p className="rounded-xl border border-dashed border-wf-line p-5 text-sm text-wf-mute">No items yet.</p>}
          {!chart && tasks.map((task) => {
            const done = !!task.completed_date;
            const overdue = !done && task.due_date && task.due_date < today;
            return (
              <div key={task.id} data-testid={`task-${task.id}`} className={cx("mb-3 rounded-2xl border p-4", overdue ? "border-wf-err" : "border-wf-line", done && "bg-wf-s2")}>
                <div className="flex items-start gap-3">
                  <button data-testid={`task-complete-${task.id}`} disabled={!canComplete(task)} title={done ? "Reopen" : "Mark complete"}
                    onClick={() => run(() => api(`/projects/${id}/tasks/${task.id}/complete`, { method: "POST", body: { completed_date: done ? "" : todayISO() } }), done ? "Reopened" : "Marked complete")}
                    className="mt-0.5 text-green-600 disabled:opacity-40">{done ? <CheckCircle2 size={22} /> : <Circle size={22} />}</button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={cx("font-bold", done && "line-through opacity-60")}>{task.title}</p>
                      <Badge label={task.priority.toUpperCase()} tone={PRIORITY_TONE[task.priority]} />
                      {overdue && <Badge label="OVERDUE" tone="over" />}
                    </div>
                    {task.description && <p className="mt-1 text-sm text-wf-ink2">{task.description}</p>}
                    <p className="mt-1 text-xs text-wf-mute">
                      {[task.lead_tech_id && `Lead: ${name(task.lead_tech_id)}`, task.helper_ids?.length && `Helpers: ${task.helper_ids.map(name).join(", ")}`,
                        task.start_date && `Start ${task.start_date}`, task.due_date && `Due ${task.due_date}`, done && `Done ${task.completed_date}`,
                        task.hours_logged ? `${num(task.hours_logged)} hrs · ${num(task.man_hours_logged)} man hrs` : null].filter(Boolean).join(" · ")}
                    </p>
                    {task.notes && <p className="mt-1 text-xs italic text-wf-ink2">{task.notes}</p>}
                    {done && !!task.approved_by_id && <p className="mt-1 text-xs font-semibold text-green-700" data-testid={`task-approved-${task.id}`}>Approved by {name(task.approved_by_id)}</p>}
                    {done && !task.approved_by_id && (
                      <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-wf-warn" data-testid={`task-awaiting-${task.id}`}>
                        <Hourglass size={13} /> Waiting for approval{p.company_pm_id ? ` from ${name(p.company_pm_id)}` : ""}
                      </p>
                    )}
                    {done && p.can_approve && (
                      <div className="mt-2 flex gap-2">
                        <Btn size="sm" variant={task.approved_by_id ? "secondary" : "primary"} icon={task.approved_by_id ? Undo2 : CheckCheck} data-testid={`task-approve-${task.id}`}
                          onClick={() => run(() => api(`/projects/${id}/tasks/${task.id}/approve`, { method: "POST", body: { approved: !task.approved_by_id } }), task.approved_by_id ? "Approval undone" : "Approved")}>
                          {task.approved_by_id ? "Undo Approval" : "Approve"}
                        </Btn>
                        {!task.approved_by_id && <Btn size="sm" variant="danger" icon={Undo2} data-testid={`task-reject-${task.id}`} onClick={() => setRejectTask(task)}>Send Back</Btn>}
                      </div>
                    )}
                    {!!task.rejection && !task.approved_by_id && (
                      <div className="mt-2 flex gap-2 rounded-xl bg-red-50 p-3 text-sm" data-testid={`task-rejection-${task.id}`}>
                        <AlertCircle size={16} className="mt-0.5 shrink-0 text-wf-err" />
                        <div><p className="font-bold text-red-800">Sent back{task.rejection.by_name ? ` by ${task.rejection.by_name}` : ""} · {new Date(task.rejection.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
                          <p className="text-red-700">{task.rejection.reason}</p></div>
                      </div>
                    )}
                    {(!!task.parts?.length || canComplete(task)) && (
                      <div className="mt-2 flex items-start gap-2 rounded-xl bg-wf-s3/60 px-3 py-2 text-sm" data-testid={`task-parts-${task.id}`}>
                        <span className="text-xs font-bold uppercase tracking-wide text-wf-mute">Parts</span>
                        <span className="flex-1 text-wf-ink2">{task.parts?.length ? task.parts.map((x) => `${x.name} × ${num(x.quantity)}`).join(" · ") : "None yet"}</span>
                        {canComplete(task) && <button data-testid={`task-edit-parts-${task.id}`} onClick={() => setPartsTask(task)} className="flex items-center gap-1 font-bold text-wf-brand"><Package size={14} />{task.parts?.length ? "Edit" : "Add"}</button>}
                      </div>
                    )}
                    <div className="grid gap-x-6 sm:grid-cols-2">
                      {["before", "after"].map((k) => (
                        <TaskPhotos key={k} t={task} kind={k} busy={busy === task.id + k} open={setViewer} onAdd={(kind, f) => addPhotos(task.id, kind, f)}
                          onRemove={(kind, path) => run(() => api(`/projects/${id}/tasks/${task.id}/photos?kind=${kind}&path=${encodeURIComponent(path)}`, { method: "DELETE" }))} />
                      ))}
                    </div>
                    <div className="mt-2 flex gap-2">
                      {isManager && <Btn size="sm" variant="ghost" data-testid={`task-edit-${task.id}`} onClick={() => nav(`/projects/${id}/tasks/${task.id}`)}>Edit</Btn>}
                      {canComplete(task) && !done && onJob && <Btn size="sm" variant="ghost" data-testid={`task-log-hours-${task.id}`} onClick={() => nav(`/work-orders/${p.work_order_id}/entry/hours?task=${task.id}`)}>Log Hours for this Item</Btn>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <MediaViewer path={viewer} onClose={() => setViewer(null)} />
      <RejectTaskDialog task={rejectTask} onClose={() => setRejectTask(null)}
        onSend={(reason) => run(() => api(`/projects/${id}/tasks/${rejectTask.id}/reject`, { method: "POST", body: { reason } }), "Sent back to the crew")} />
      <TaskPartsDialog task={partsTask} onClose={() => setPartsTask(null)}
        onSave={(parts) => run(() => api(`/projects/${id}/tasks/${partsTask.id}/parts`, { method: "PUT", body: { parts } }), "Parts saved and added to the job")} />
      <Confirm open={confirm} title="Remove this project?" message="The job stays; only the project plan is removed." confirmLabel="Remove"
        onConfirm={async () => { try { await api(`/projects/${id}`, { method: "DELETE" }); qc.invalidateQueries({ queryKey: ["projects"] }); nav(`/work-orders/${p.work_order_id}`); } catch (e) { toast.error(e.message); } }}
        onClose={() => setConfirm(false)} />
    </Page>
  );
}

const emptyTask = { lead_tech_id: "", helper_ids: [], priority: "medium", title: "", description: "", notes: "", start_date: "", due_date: "", completed_date: "", approved_by_id: "" };

export function TaskForm() {
  const { pid, tid } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const project = useQuery({ queryKey: ["project", pid], queryFn: () => api(`/projects/${pid}`) });
  const emps = useEmployees();
  const task = project.data?.tasks.find((t) => t.id === tid);
  const [f, setF] = useState(emptyTask);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    if (task) {
      const { id: _i, before_photos, after_photos, hours_logged, man_hours_logged, approved_at, rejection, parts, ...rest } = task;
      setF({ ...emptyTask, ...rest });
    }
  }, [task]);
  const done = (p, msg) => {
    qc.setQueryData(["project", pid], p);
    qc.invalidateQueries({ queryKey: ["projects"] });
    toast.success(msg);
    nav(`/projects/${pid}`, { replace: true });
  };
  const save = async () => {
    if (!f.title.trim()) return toast.error("Enter the job to complete");
    setSaving(true);
    try {
      done(await api(`/projects/${pid}/tasks${tid ? `/${tid}` : ""}`, { method: tid ? "PUT" : "POST", body: { ...f, title: f.title.trim() } }), tid ? "Item updated" : "Item added");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const people = (emps.data ?? []).filter((e) => (e.status ?? "active") === "active").map((e) => ({ value: e.id, label: e.name }));
  if (tid && project.isLoading) return <Loading />;
  return (
    <Page title={tid ? "Edit Item" : "New Item"} back testid="task-form"
      right={<>
        {tid && <IconBtn icon={Trash2} tone="danger" onClick={() => setConfirm(true)} data-testid="task-delete-button" />}
        <Btn icon={tid ? Save : Plus} loading={saving} onClick={save} data-testid="task-save-button">{tid ? "Save" : "Add Item"}</Btn>
      </>}>
      <div className="max-w-2xl">
        <Field label="Job To Complete *" testid="task-title-input" value={f.title} onChange={set("title")} placeholder="Run cable to 3rd floor IDF" />
        <Field label="Description of the Job" testid="task-description-input" value={f.description} onChange={set("description")} multiline />
        <p className="mb-2 text-[13px] font-semibold">Priority</p>
        <div className="mb-4 flex gap-2">
          {["high", "medium", "low"].map((p) => <Chip key={p} label={p[0].toUpperCase() + p.slice(1)} selected={f.priority === p} onClick={() => set("priority")(p)} testid={`task-priority-${p}`} />)}
        </div>
        <Select label="Lead Technician" testid="task-lead-select" value={f.lead_tech_id} placeholder="None" options={people} onChange={set("lead_tech_id")} />
        <p className="mb-2 text-[13px] font-semibold">Helpers</p>
        <div className="mb-4 flex flex-wrap gap-2">
          {people.map((p) => {
            const on = f.helper_ids.includes(p.value);
            return <Chip key={p.value} label={p.label} selected={on} testid={`task-helper-${p.value}`} onClick={() => set("helper_ids")(on ? f.helper_ids.filter((x) => x !== p.value) : [...f.helper_ids, p.value])} />;
          })}
        </div>
        <div className="grid gap-x-3 sm:grid-cols-3">
          <Field label="Start Date" type="date" testid="task-start-input" value={f.start_date} onChange={set("start_date")} />
          <Field label="Due Date" type="date" testid="task-due-input" value={f.due_date} onChange={set("due_date")} />
          <Field label="Completed Date" type="date" testid="task-completed-input" value={f.completed_date} onChange={set("completed_date")} />
        </div>
        <Select label="Approved By" testid="task-approved-select" value={f.approved_by_id} placeholder="None" options={people} onChange={set("approved_by_id")} />
        <Field label="Notes" testid="task-notes-input" value={f.notes} onChange={set("notes")} multiline />
      </div>
      <Confirm open={confirm} title="Delete this item?" onClose={() => setConfirm(false)}
        onConfirm={async () => { try { done(await api(`/projects/${pid}/tasks/${tid}`, { method: "DELETE" }), "Item deleted"); } catch (e) { toast.error(e.message); } }} />
    </Page>
  );
}
